import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {Box, Typography, Grid, Button, IconButton, Avatar, Modal} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { LeagueSeason, League, TeamSeason, User, TopBottomGamesReport, TeamWeek, TeamBudgetReport } from '../../../sdk/model';
import { leagueGameApi, reportingApi } from '../../../adapters/APIExporter';
import UserInfoCard from '../../cards/userCard'
import { toSalary } from '../../util/SalaryDisplay';

interface allSeasonsReportProps {
  league: League,
  completedSeasons: LeagueSeason[],
  showSeason: Function
}

function AllSeasonsReport(props: allSeasonsReportProps) {
  const navigate = useNavigate();
  const [viewingUser, setViewingUser] = React.useState<User | undefined>(undefined);
  const [topBottomGamesReport, setTopBottomGamesReport] = React.useState<TopBottomGamesReport | undefined>(undefined);
  const [teamBudgetReports, setTeamBudgetReports] = React.useState<TeamBudgetReport[]>([]);
  const recordColumns = React.useMemo(
    () => [
      {
        accessorKey: 'team.name',
        header: 'Team Name',
        size: 25,
        Cell: ({ renderedCellValue, row }) => <Button onClick={() => {
          showTeam(row.original.team.id);
          }}>{renderedCellValue}</Button>
      },
      {
        accessorKey: 'wins', //access nested data with dot notation
        header: 'Wins',
        size: 15,
      },
      {
        accessorKey: 'losses',
        header: 'Losses',
        size: 15,
      },
      {
        accessorKey: 'ties', //normal accessorKey
        header: 'Ties',
        size: 15,
      },
      {
        accessorKey: 'winPct',
        id: 'winPct',
        header: 'Win %',
        size: 15,
        Cell: ({ renderedCellValue, row }) => [-1, 99].includes(renderedCellValue) ? '-' : ((renderedCellValue * 100).toFixed(1)) + "%"
      },
    ], []
  );
  const pointsColumns = React.useMemo(
      () => [
        {
          accessorKey: 'team.name',
          header: 'Team Name',
          size: 25,
          Cell: ({ renderedCellValue, row }) => <Button onClick={() => {
            showTeam(row.original.team.id);
            }}>{renderedCellValue}</Button>
        },
        {
          accessorKey: 'pointsScored', //access nested data with dot notation
          header: 'Points For',
          size: 15,
          Cell: ({ renderedCellValue }) => renderedCellValue.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}),
        },
        {
          accessorKey: 'pointsAgainst',
          header: 'Points Against',
          size: 15,
          Cell: ({ renderedCellValue }) => renderedCellValue.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}),
        },
      ], []
    );
    const teamBudgetColumns = React.useMemo(
      () => [
        {
          accessorKey: 'team.name',
          header: 'Team Name',
          size: 25,
          Cell: ({ renderedCellValue, row }) => <Button onClick={() => {
            showTeam(row.original.team.id);
            }}>{renderedCellValue}</Button>
        },
        {
          accessorKey: 'totalMoneySpent',
          header: 'Total Spent',
          size: 15,
          Cell: ({ renderedCellValue }) => toSalary(renderedCellValue)
        },
        {
          accessorKey: 'costPerWin',
          id: 'costPerWin',
          header: 'Cost/Win',
          size: 15,
          Cell: ({ renderedCellValue }) => toSalary(renderedCellValue),
        },
        {
          accessorKey: 'costPerPoint',
          header: 'Cost/Point',
          size: 15,
          Cell: ({ renderedCellValue }) => toSalary(renderedCellValue),
        },
      ], []
    );
    
  React.useEffect(() => {
    leagueGameApi.getTopBottomWeeks(props.league.id!).then(response => setTopBottomGamesReport(response.data))
    reportingApi.getLeagueBudgetReport(props.league.id!).then(response => setTeamBudgetReports(response.data))
  }, [])
  function showTeam(teamId : string) {
    navigate(`/league/${props.league.id}/team/${teamId}`)
  }
  function getCountByTeamDisplay(propertyName) {
    const countMap = props.league.teams
      .reduce((accumulator, team) => {
        const championships = team[propertyName].length;
        if (accumulator[championships]) {
          accumulator[championships].push(team);
        } else {
          accumulator[championships] = [team];
        }
        return accumulator;
      }, {});
    var ranking = 1;
    return Object.keys(countMap).sort().reverse().map(championships => {
      const elem = countMap[championships].map(team => <Grid key={team.id} container spacing={2} justifyContent="center">
        <Grid item textAlign="right" xs={5}>
          <Box sx={{marginTop: '5px'}}>{ranking}.</Box>
        </Grid>
        <Grid item textAlign="left" xs={7}>
          <Button onClick={() => showTeam(team.id)}>{team.name}</Button> - {championships}
        </Grid>
      </Grid>)
      ranking += countMap[championships].length;
      return elem;
    });
  }
  
  const allTeamSeasons : TeamSeason[] = props.completedSeasons.flatMap(season => season.teams ?? []).concat(props.league.teams.map(team => team.currentSeason!));
  function getTeamRecordList(winsAttribute, lossesAttribute, tiesAttribute, reversed) {
    const teamRecords = allTeamSeasons.reduce((accumulator, season) => {
      if (!accumulator[season.teamId!]) {
        const thisTeam = props.league.teams.find(team => season.teamId === team.id)
        accumulator[season.teamId!] = {team: thisTeam, wins: 0, losses: 0, ties: 0};
      }
      const teamRecord = accumulator[season.teamId!];
      teamRecord.wins += season[winsAttribute];
      teamRecord.losses += season[lossesAttribute];
      if (tiesAttribute) {
        teamRecord.ties += season[tiesAttribute];
      }
      return accumulator;
    }, {});
    const teamRecordsList = Object.values(teamRecords);
    teamRecordsList.forEach(teamRecord => {
      const totalGames = teamRecord.wins + teamRecord.losses + teamRecord.ties;
      teamRecord.winPct = totalGames === 0 ? (reversed ? 99 : -1) : (teamRecord.wins + teamRecord.ties / 2) / totalGames;
    });
    return teamRecordsList;
  }
  function getTop5(valueFunction, reversed) {
    const topSeasons = [];
    const seasonWinPctgs = props.completedSeasons.flatMap(season => season.teams).reduce((accumulator, currentValue) => {
      const value = valueFunction(currentValue);
      if (!accumulator![value]) {
        accumulator![value] = []
      }
      accumulator![value].push(currentValue)
      return accumulator
    }, {})!;
    let rank = 1;
    let rankedValues = Object.keys(seasonWinPctgs).sort();
    if (reversed) {
      rankedValues = rankedValues.reverse();
    }
    let index = 0;
    while (rank <= 5) {
      const value = rankedValues[index];
      seasonWinPctgs[value].forEach(season => topSeasons.push({rank: rank, value: value, season: season}))
      rank += seasonWinPctgs[value].length;
      index++;
    }
    return topSeasons;
  }
  function getWinPctg(season) {
    return (season!.wins! + season!.ties! / 2) / (season!.wins! + season!.ties! + season!.losses!)
  }
  function getPointsScored(season) {
      return season.pointsScored;
    }
  const teamRecordList = getTeamRecordList("wins", "losses", "ties", false);
  const teamPlayoffRecordList = getTeamRecordList("playoffWins", "playoffLosses", undefined, false);
  const loserBracketRecordList = getTeamRecordList("loserBracketWins", "loserBracketLosses", undefined, true);
  const teamPostseasonRecordList = getTeamRecordList("postseasonWins", "postseasonLosses", undefined, false);
  const teamPoints =   allTeamSeasons.reduce((accumulator, season) => {
    if (!accumulator[season.teamId!]) {
      const thisTeam = props.league.teams.find(team => season.teamId === team.id)
      accumulator[season.teamId!] = {team: thisTeam, pointsScored: 0, pointsAgainst: 0};
    }
    const teamPoints = accumulator[season.teamId!];
    teamPoints.pointsScored += season.pointsScored;
    teamPoints.pointsAgainst += season.pointsAgainst;
    return accumulator;
  }, {});
  const teamPointsList = Object.values(teamPoints);
  const topRecordSeasons = getTop5(getWinPctg, true);
  const bottomRecordSeasons = getTop5(getWinPctg, false);
  const topPointsSeasons = getTop5(getPointsScored, true);
  const bottomPointsSeasons = getTop5(getPointsScored, false);
  
  function getGamesReport(title: string, rankedWeeks: Array<Array<TeamWeek>>) {
    return <Grid item>
      <Box sx={{ border: 1, maxWidth: 600, fontSize: 12 }}>
        <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        {rankedWeeks.map((rankingList, rankingIndex) => rankingList.map((teamWeek, teamWeekIndex) => <Grid key={teamWeekIndex} container spacing={2} justifyContent="center">
          <Grid item textAlign="right" xs={1}>
            <Box sx={{marginTop: '5px'}}>{rankingIndex + 1}.</Box>
          </Grid>
          <Grid item textAlign="left" xs={3}>
            <Button sx={{fontSize: 12}} onClick={() => showTeam(teamWeek.team?.teamId!)}>{teamWeek.team?.teamName}</Button>
          </Grid>
          <Grid item textAlign="left" xs={3}>
            <Button sx={{fontSize: 12}} onClick={() => navigate(`/league/${props.league.id}/matchup/id/${teamWeek.leagueGameId}`)}>{teamWeek.seasonYear}, Week {teamWeek.weekNumber}</Button>
          </Grid>
          <Grid item textAlign="left" xs={5}>
            {Number(teamWeek.points).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} vs {teamWeek.opposingTeam ? <Button sx={{fontSize: 12}} onClick={() => showTeam(teamWeek.opposingTeam?.teamId!)}>{teamWeek.opposingTeam.teamName}</Button> : 'BYE'}
          </Grid>
        </Grid>))}
      </Box>
    </Grid>
  }

  return <Box>
    <Modal open={!!viewingUser} onClose={() => setViewingUser(undefined)} 
        aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description"
        sx={{margin: '5%'}}>
      <UserInfoCard userId={viewingUser?.id!} callback={() => setViewingUser(undefined)} />
    </Modal>
    <Typography variant="h4" component="div" sx={{ flexGrow: 1 }}>
      Team Accomplishments
    </Typography>
    <Grid container spacing={2} justifyContent="center">
      <Grid item>
        <Box sx={{ border: 1, width: 600 }}>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
            League Champions:
          </Typography>
          {props.completedSeasons.map(season => {
            const championshipTeams = season.teams?.filter(team => team.overallRank === 1) || [];
            return <Grid key={season.seasonYear} container spacing={2} justifyContent="center">
              <Grid item textAlign="right" xs={5}>
                <Box><Button onClick={() => props.showSeason(season.seasonYear!)}>{season.seasonYear}</Button>:</Box>
              </Grid>
              <Grid item textAlign="left" xs={7}>
                {championshipTeams.map(team => <Box key={team.id}>
                  <Button onClick={() => showTeam(team.teamId!)}>{team.teamName}</Button> - 
                  {team.owners?.map((owner, ownerIndex) => 
                    <Button key={ownerIndex} onClick={() => setViewingUser(owner)}>
                      <IconButton sx={{ p: 0 }}>
                        <Avatar alt={owner?.username} src={owner?.picture} />
                      </IconButton>
                      {' ' + owner.username}
                      <br/>
                    </Button>
                  )}
                </Box>)}
              </Grid>
            </Grid>
          })}
        </Box>
        <Box sx={{ border: 1, width: 600 }}>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
            Championships by Team:
          </Typography>
          {getCountByTeamDisplay('leagueChampionshipSeasons')}
        </Box>
      </Grid>
      <Grid item>
        <Box sx={{ border: 1, width: 600 }}>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
            Division Championships by Team:
          </Typography>
          {getCountByTeamDisplay('divisionChampionshipSeasons')}
        </Box>
        <Box sx={{ border: 1, width: 600 }}>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
            Playoff Appearances by Team:
          </Typography>
          {getCountByTeamDisplay('playoffAppearanceSeasons')}
        </Box>
      </Grid>
    </Grid>
    <Typography variant="h4" component="div" sx={{ flexGrow: 1 }}>
      League Records
    </Typography>
    <Grid container spacing={2} justifyContent="center">
      <Grid item>
        <Box sx={{ border: 1, maxWidth: 600 }}>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
            Regular Season Records
          </Typography>
          <MaterialReactTable enableMultiRowSelection={false}
              getRowId={(row) => row.id} columns={recordColumns} 
              data={teamRecordList} enablePagination={false}
              initialState={{
                sorting: [
                  {
                    id: 'winPct',
                    desc: true,
                  },
                  {
                    id: 'wins',
                    desc: true,
                  }
                ],
                
              }}/>
        </Box>
      </Grid>
      <Grid item>
        <Box sx={{ border: 1, maxWidth: 600 }}>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
            Playoff Records
          </Typography>
          <MaterialReactTable enableMultiRowSelection={false}
              getRowId={(row) => row.id} columns={recordColumns} 
              data={teamPlayoffRecordList} enablePagination={false} 
              initialState={{
                sorting: [
                  {
                    id: 'winPct',
                    desc: true,
                  },
                  {
                    id: 'wins',
                    desc: true,
                  },
                  {
                    id: 'losses',
                    desc: false,
                  }
                ],
                
              }}/>
        </Box>
      </Grid>
      <Grid item>
        <Box sx={{ border: 1, maxWidth: 600 }}>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
            Loser Bracket Records
          </Typography>
          <MaterialReactTable enableMultiRowSelection={false}
              getRowId={(row) => row.id} columns={recordColumns} 
              data={loserBracketRecordList} enablePagination={false} 
              initialState={{
                sorting: [
                  {
                    id: 'winPct',
                    desc: false,
                  },
                  {
                    id: 'losses',
                    desc: true,
                  },
                  {
                    id: 'wins',
                    desc: false,
                  }
                ],
                
              }}/>
        </Box>
      </Grid>
      <Grid item>
        <Box sx={{ border: 1, maxWidth: 600 }}>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
            Postseason Records
          </Typography>
          <MaterialReactTable enableMultiRowSelection={false}
              getRowId={(row) => row.id} columns={recordColumns} 
              data={teamPostseasonRecordList} enablePagination={false} 
              initialState={{
                sorting: [
                  {
                    id: 'winPct',
                    desc: true,
                  },
                  {
                    id: 'wins',
                    desc: true,
                  }
                ],
                
              }}/>
        </Box>
      </Grid>
      <Grid item>
        <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
          Points Scored, All-Time
        </Typography>
        <Box sx={{ border: 1, maxWidth: 600 }}>
          <MaterialReactTable enableMultiRowSelection={false}
            getRowId={(row) => row.id} columns={pointsColumns} 
            data={teamPointsList} enablePagination={false} 
            initialState={{
              sorting: [
                {
                  id: 'pointsScored',
                  desc: true,
                }
              ],
              
            }}/>
        </Box>
      </Grid>
    </Grid>
    <Typography variant="h4" component="div" sx={{ flexGrow: 1 }}>
      Top/Bottom 5
    </Typography>
    <Grid container spacing={2} justifyContent="center" sx={{ fontSize: '18px' }}>
      <Grid item>
        <Box sx={{ border: 1, maxWidth: 600 }}>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
            Top 5 Seasons (Record)
          </Typography>
          {topRecordSeasons.map((seasonInfo, seasonIndex) => <Grid key={seasonIndex} container spacing={2} justifyContent="center">
            <Grid item textAlign="right" xs={2}>
              <Box sx={{marginTop: '5px'}}>{seasonInfo.rank}.</Box>
            </Grid>
            <Grid item textAlign="left" xs={5}>
              <Button onClick={() => showTeam(seasonInfo.season.teamId)}>{seasonInfo.season.teamName}</Button> - {seasonInfo.season.seasonYear}
            </Grid>
            <Grid item textAlign="left" xs={5}>
              {((seasonInfo.value * 100).toFixed(1)) + "%"} ({seasonInfo.season.wins}-{seasonInfo.season.losses}-{seasonInfo.season.ties})
            </Grid>
          </Grid>)}
        </Box>
      </Grid>
      <Grid item>
        <Box sx={{ border: 1, maxWidth: 600 }}>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
            Bottom 5 Seasons (Record)
          </Typography>
          {bottomRecordSeasons.map((seasonInfo, seasonIndex) => <Grid key={seasonIndex} container spacing={2} justifyContent="center">
            <Grid item textAlign="right" xs={2}>
              <Box sx={{marginTop: '5px'}}>{seasonInfo.rank}.</Box>
            </Grid>
            <Grid item textAlign="left" xs={5}>
              <Button onClick={() => showTeam(seasonInfo.season.teamId)}>{seasonInfo.season.teamName}</Button> - {seasonInfo.season.seasonYear}
            </Grid>
            <Grid item textAlign="left" xs={5}>
              {((seasonInfo.value * 100).toFixed(1)) + "%"} ({seasonInfo.season.wins}-{seasonInfo.season.losses}-{seasonInfo.season.ties})
            </Grid>
          </Grid>)}
        </Box>
      </Grid>
      <Grid item>
        <Box sx={{ border: 1, maxWidth: 600 }}>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
            Top 5 Seasons (Points)
          </Typography>
          {topPointsSeasons.map((seasonInfo, seasonIndex) => <Grid key={seasonIndex} container spacing={2} justifyContent="center">
            <Grid item textAlign="right" xs={2}>
              <Box sx={{marginTop: '5px'}}>{seasonInfo.rank}.</Box>
            </Grid>
            <Grid item textAlign="left" xs={5}>
              <Button onClick={() => showTeam(seasonInfo.season.teamId)}>{seasonInfo.season.teamName}</Button> - {seasonInfo.season.seasonYear}
            </Grid>
            <Grid item textAlign="left" xs={5}>
              {Number(seasonInfo.value).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </Grid>
          </Grid>)}
        </Box>
      </Grid>
      <Grid item>
        <Box sx={{ border: 1, maxWidth: 600 }}>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
            Bottom 5 Seasons (Points)
          </Typography>
          {bottomPointsSeasons.map((seasonInfo, seasonIndex) => <Grid key={seasonIndex} container spacing={2} justifyContent="center">
            <Grid item textAlign="right" xs={2}>
              <Box sx={{marginTop: '5px'}}>{seasonInfo.rank}.</Box>
            </Grid>
            <Grid item textAlign="left" xs={5}>
              <Button onClick={() => showTeam(seasonInfo.season.teamId)}>{seasonInfo.season.teamName}</Button> - {seasonInfo.season.seasonYear}
            </Grid>
            <Grid item textAlign="left" xs={5}>
              {Number(seasonInfo.value).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </Grid>
          </Grid>)}
        </Box>
      </Grid>
      {topBottomGamesReport ? 
        <React.Fragment>
          {getGamesReport("Top 5 Weeks", topBottomGamesReport.topFive!)}
          {getGamesReport("Bottom 5 Weeks", topBottomGamesReport.bottomFive!)}
        </React.Fragment> : <Typography>Loading Game Data...</Typography>
      }
    </Grid>
    <Typography variant="h4" component="div" sx={{ flexGrow: 1 }}>
      Budget Efficiency
    </Typography>
    <Grid container spacing={2} justifyContent="center">
      <Grid item>
        <Box sx={{ border: 1, maxWidth: 900 }}>
          <MaterialReactTable enableMultiRowSelection={false}
              getRowId={(row) => row.id} columns={teamBudgetColumns} 
              data={teamBudgetReports} enablePagination={false}
              initialState={{
                sorting: [
                  {
                    id: 'costPerWin',
                    desc: false,
                  },
                ],
                
              }}/>
        </Box>
      </Grid>
    </Grid>
  </Box>
}

export default AllSeasonsReport;
